// src/pages/school/Students.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container, Row, Col, Card, Table, Button, ButtonGroup, Dropdown,
  Form, InputGroup, Modal, FloatingLabel, Badge, Spinner, ProgressBar
} from "react-bootstrap";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import {
  collection, addDoc, updateDoc, deleteDoc, doc,
  onSnapshot, serverTimestamp, query, orderBy, setDoc
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../services/firebase";
import useSchool from "../../hooks/useSchool";

import { getApps, initializeApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  updateProfile,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";

/* ---------------- helpers ---------------- */
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const fmt = (d, f = "DD MMM YYYY") =>
  d && dayjs(d).isValid() ? dayjs(d).format(f) : "—";
const initials = (name = "") =>
  name.trim().split(/\s+/, 2).map((p) => p[0]?.toUpperCase() || "").join("");

const randomPass = (len = 10) =>
  Array.from(crypto.getRandomValues(new Uint32Array(len)))
    .map((n) => "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789@#$%".charAt(n % 64))
    .join("");

const defaultPassFrom = (rollNo, admissionNo) => {
  const seed = String(rollNo || admissionNo || "").trim().replace(/\s+/g, "");
  return seed ? `Student@${seed}` : `Student@${randomPass(6)}`;
};

// Create Auth user on a secondary app so current session doesn’t get clobbered
async function createStudentAuthUser({ email, password, displayName }) {
  const apps = getApps();
  const primary = apps[0];
  const options = primary?.options;

  let secondaryAuth;
  if (options) {
    const secondaryApp =
      apps.find((a) => a.name === "secondary") || initializeApp(options, "secondary");
    secondaryAuth = getAuth(secondaryApp);
  } else {
    secondaryAuth = getAuth();
  }

  const cred = await createUserWithEmailAndPassword(secondaryAuth, email, password);
  try { if (displayName) await updateProfile(cred.user, { displayName }); } catch {}
  try { await signOut(secondaryAuth); } catch {}
  return cred.user; // { uid, email }
}

/* ---------- Extended class options ---------- */
const CLASS_OPTIONS = [
  ...Array.from({ length: 12 }, (_, i) => `Class ${i + 1}`),

  // Class 12 specialization streams
  "Class 12 PCM","Class 12 PCB","Class 12 PCMB","Class 12 Commerce","Class 12 Arts",

  // Foundation
  "Foundation 6","Foundation 7","Foundation 8","Foundation 9","Foundation 10",

  // Competitive prep
  "NEET","JEE",
  "Class 11 NEET","Class 11 JEE","Class 12 NEET","Class 12 JEE",

  // Repeaters / Others
  "Integrated","LT","Repeaters NEET","Repeaters JEE",

  // Undergraduate (UG)
  "B.Tech","BE","B.Sc","B.Com","BA","BBA","BCA",

  // Medical / Allied Health
  "MBBS","BDS","Pharmacy","Nursing",

  // Vocational
  "Diploma / Polytechnic","ITI",

  // Misc
  "Other Higher Education"
];

/* ---------------- form modal (single) ---------------- */
const DEFAULT = {
  name: "", rollNo: "", className: "", section: "", branch: "", gender: "", dob: "",
  phone: "", email: "", address: "", admissionNo: "", status: "active",
  photoFile: null, removePhoto: false,
  password: "",
};

function StudentFormModal({ show, onHide, initial, onSave }) {
  const [form, setForm] = useState(DEFAULT);
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (initial) {
      setForm({
        name: initial.name || "",
        rollNo: initial.rollNo || "",
        className: initial.className || "",
        section: initial.section || "",
        branch: initial?.branch || "",
        gender: initial.gender || "",
        dob: initial.dob ? dayjs(toDate(initial.dob)).format("YYYY-MM-DD") : "",
        phone: initial.phone || "",
        email: initial.email || "",
        address: initial.address || "",
        admissionNo: initial.admissionNo || "",
        status: initial.status || "active",
        photoFile: null,
        removePhoto: false,
        password: "",
      });
    } else {
      setForm(DEFAULT);
    }
  }, [initial, show]);

  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Set sensible branch defaults when class changes
  const handleClassChange = (value) => {
    const class12Streams = ["Class 12 PCM","Class 12 PCB","Class 12 PCMB","Class 12 Commerce","Class 12 Arts"];
    let updates = { className: value };

    if (class12Streams.includes(value)) {
      // Auto-fill stream name as branch
      const stream = value.replace("Class 12 ", "");
      updates.branch = stream;
    } else if (["B.Tech","BE","B.Sc","B.Com","BA","BBA","BCA","Diploma / Polytechnic"].includes(value)) {
      // Keep previous branch if it still exists in the options; else clear
      const opts = branchOptionsFor(value);
      if (!opts?.includes(form.branch)) updates.branch = "";
    } else {
      // Not a stream/UG — keep manual branch as-is
    }
    setForm((f) => ({ ...f, ...updates }));
  };

  const rollSeed = (form.rollNo || form.admissionNo || "0000").toString().trim().replace(/\s+/g, "");
  const previewDefaultPassword = `Student@${rollSeed || "roll"}`;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return Swal.fire({ icon: "warning", title: "Name is required" });
    if (!form.email.trim()) return Swal.fire({ icon: "warning", title: "Email is required" });

    setSaving(true);
    try {
      const finalEmail = form.email.trim().toLowerCase();
      const finalPassword = (form.password || "").trim() || defaultPassFrom(form.rollNo, form.admissionNo);

      await onSave({
        name: form.name.trim(),
        rollNo: String(form.rollNo || "").trim(),
        className: String(form.className || "").trim(),
        section: String(form.section || "").trim(),
        branch: String(form.branch || "").trim(),
        gender: String(form.gender || "").trim(),
        dob: form.dob || null,
        phone: String(form.phone || "").trim(),
        email: finalEmail,
        password: finalPassword,
        address: String(form.address || "").trim(),
        admissionNo: String(form.admissionNo || "").trim(),
        status: String(form.status || "active"),
        photoFile: form.photoFile || null,
        removePhoto: !!form.removePhoto,
      });
      onHide?.();
    } finally {
      setSaving(false);
    }
  };

  const previewUrl = form.photoFile ? URL.createObjectURL(form.photoFile) : (initial?.photoURL || "");
  const acceptFile = (file) => {
    if (!file) return;
    if (!/image\/(png|jpeg|webp)/.test(file.type)) {
      Swal.fire({ icon: "info", title: "Please choose PNG/JPG/WEBP" });
      return;
    }
    change("photoFile", file);
    change("removePhoto", false);
  };

  // Branch options generator
  const branchOptionsFor = (className) => {
    if (["B.Tech","BE","Diploma / Polytechnic"].includes(className)) {
      return ["CSE","IT","ECE","EEE","Mechanical","Civil","AI/ML","Data Science","Biotech","Chemical","Mechatronics","Automobile","Other"];
    }
    if (className === "B.Sc") {
      return ["Physics","Chemistry","Mathematics","Computer Science","Biotechnology","Zoology","Botany","Statistics","Other"];
    }
    if (className === "B.Com") {
      return ["General","Accounting & Finance","Banking & Insurance","Computer Applications","Corporate Secretaryship","Other"];
    }
    if (className === "BA") {
      return ["English","Economics","History","Political Science","Sociology","Psychology","Tamil","Other"];
    }
    if (["BBA"].includes(className)) {
      return ["General","Finance","Marketing","HR","Business Analytics","Logistics","Other"];
    }
    if (["BCA"].includes(className)) {
      return ["General","Data Science","AI/ML","Cloud Computing","Cyber Security","Other"];
    }
    if (["Class 12 PCM","Class 12 PCB","Class 12 PCMB","Class 12 Commerce","Class 12 Arts"].includes(className)) {
      return ["PCM","PCB","PCMB","Commerce","Arts"];
    }
    return null; // free-text
  };

  const branchOptions = branchOptionsFor(form.className);
  const isClass12Stream = ["Class 12 PCM","Class 12 PCB","Class 12 PCMB","Class 12 Commerce","Class 12 Arts"].includes(form.className);

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>{initial ? "Edit Student" : "Add Student"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-3">
            <Col lg={8}>
              <Row className="g-3">
                <Col md={6}>
                  <FloatingLabel label="Full Name *">
                    <Form.Control value={form.name} onChange={(e)=>change("name", e.target.value)} placeholder="Student name" required />
                  </FloatingLabel>
                </Col>
                <Col md={3}>
                  <FloatingLabel label="Roll No.">
                    <Form.Control value={form.rollNo} onChange={(e)=>change("rollNo", e.target.value)} placeholder="42" />
                  </FloatingLabel>
                </Col>
                <Col md={3}>
                  <FloatingLabel label="Admission No.">
                    <Form.Control value={form.admissionNo} onChange={(e)=>change("admissionNo", e.target.value)} placeholder="ADM123" />
                  </FloatingLabel>
                </Col>

                <Col md={4}>
                  <FloatingLabel label="Class">
                    <Form.Select value={form.className} onChange={(e)=>handleClassChange(e.target.value)}>
                      <option value="">— Select class —</option>

                      <optgroup label="School Classes">
                        {CLASS_OPTIONS
                          .filter(x => x.startsWith("Class ") && /^\d+$/.test(x.replace("Class ", "")))
                          .map(x => <option key={x} value={x}>{x}</option>)}
                      </optgroup>

                      <optgroup label="Class 12 Streams">
                        {["Class 12 PCM","Class 12 PCB","Class 12 PCMB","Class 12 Commerce","Class 12 Arts"].map(x => (
                          <option key={x} value={x}>{x}</option>
                        ))}
                      </optgroup>

                      <optgroup label="Foundation (6–10)">
                        {CLASS_OPTIONS.filter(x => x.startsWith("Foundation ")).map(x => (
                          <option key={x} value={x}>{x}</option>
                        ))}
                      </optgroup>

                      <optgroup label="Competitive (General)">
                        {["NEET","JEE"].map(x => <option key={x} value={x}>{x}</option>)}
                      </optgroup>

                      <optgroup label="Competitive (Class 11–12)">
                        {["Class 11 NEET","Class 11 JEE","Class 12 NEET","Class 12 JEE"].map(x => (
                          <option key={x} value={x}>{x}</option>
                        ))}
                      </optgroup>

                      <optgroup label="Undergraduate (UG)">
                        {["B.Tech","BE","B.Sc","B.Com","BA","BBA","BCA"].map(x => <option key={x} value={x}>{x}</option>)}
                      </optgroup>

                      <optgroup label="Medical / Allied Health">
                        {["MBBS","BDS","Pharmacy","Nursing"].map(x => <option key={x} value={x}>{x}</option>)}
                      </optgroup>

                      <optgroup label="Vocational">
                        {["Diploma / Polytechnic","ITI"].map(x => <option key={x} value={x}>{x}</option>)}
                      </optgroup>

                      <optgroup label="Other">
                        {["Integrated","LT","Repeaters NEET","Repeaters JEE","Other Higher Education"].map(x => <option key={x} value={x}>{x}</option>)}
                      </optgroup>

                      {form.className && !CLASS_OPTIONS.includes(form.className) && !/^\d+$/.test(form.className) && (
                        <option value={form.className}>{form.className} (custom)</option>
                      )}
                    </Form.Select>
                  </FloatingLabel>
                </Col>

                <Col md={4}>
                  <FloatingLabel label="Section">
                    <Form.Control value={form.section} onChange={(e)=>change("section", e.target.value)} placeholder="A" />
                  </FloatingLabel>
                </Col>

                <Col md={4}>
                  <FloatingLabel label={isClass12Stream ? "Stream" : "Branch"}>
                    {branchOptions ? (
                      <Form.Select
                        value={form.branch}
                        onChange={(e) => change("branch", e.target.value)}
                      >
                        <option value="">— Select {isClass12Stream ? "Stream" : "Branch"} —</option>
                        {branchOptions.map((b) => (
                          <option key={b} value={b}>{b}</option>
                        ))}
                      </Form.Select>
                    ) : (
                      <Form.Control
                        value={form.branch}
                        onChange={(e) => change("branch", e.target.value)}
                        placeholder="CSE / ECE / Mechanical (optional)"
                      />
                    )}
                  </FloatingLabel>
                </Col>

                <Col md={4}>
                  <FloatingLabel label="Status">
                    <Form.Select value={form.status} onChange={(e)=>change("status", e.target.value)}>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Form.Select>
                  </FloatingLabel>
                </Col>

                <Col md={4}>
                  <FloatingLabel label="Gender">
                    <Form.Select value={form.gender} onChange={(e)=>change("gender", e.target.value)}>
                      <option value="">—</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </Form.Select>
                  </FloatingLabel>
                </Col>
                <Col md={4}>
                  <FloatingLabel label="DOB">
                    <Form.Control type="date" value={form.dob} onChange={(e)=>change("dob", e.target.value)} />
                  </FloatingLabel>
                </Col>
                <Col md={4}>
                  <FloatingLabel label="Phone">
                    <Form.Control value={form.phone} onChange={(e)=>change("phone", e.target.value)} placeholder="+91 90000 00000" />
                  </FloatingLabel>
                </Col>

                <Col md={8}>
                  <FloatingLabel label="Email">
                    <Form.Control type="email" value={form.email} onChange={(e)=>change("email", e.target.value)} placeholder="student@school.com" />
                  </FloatingLabel>
                </Col>

                {/* Password input: used only when creating Auth account */}
                {(!initial || (initial && !initial.auth?.uid)) && (
                  <Col md={4}>
                    <Form.Label className="mb-1">Password</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type={showPw ? "text" : "password"}
                        value={form.password}
                        placeholder={previewDefaultPassword}
                        onChange={(e) => change("password", e.target.value)}
                        autoComplete="new-password"
                      />
                      <Button
                        variant="outline-secondary"
                        onClick={() => setShowPw((v) => !v)}
                        title={showPw ? "Hide password" : "Show password"}
                        tabIndex={-1}
                      >
                        <i className={`bi ${showPw ? "bi-eye-slash" : "bi-eye"}`} />
                      </Button>
                    </InputGroup>
                    <div className="form-text">
                      Leave blank to use <code>{previewDefaultPassword}</code>.
                    </div>
                  </Col>
                )}

                {/* Account hints */}
                {initial ? (
                  <Col md={12}>
                    <Card className="border-0 mt-1">
                      <Card.Body className="p-3 border rounded-3">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <div className="fw-semibold mb-1">Account</div>
                            {initial.auth?.uid ? (
                              <>
                                <div className="small text-muted">
                                  <div><b>Status:</b> Active</div>
                                  <div><b>Login ID:</b> <code>{initial.auth?.email || initial.email || form.email || "—"}</code></div>
                                </div>
                                <div className="small text-muted mt-2">
                                  For security, passwords can’t be viewed. Use <i>Reset password</i> to email a reset link.
                                </div>
                              </>
                            ) : (
                              <div className="small text-muted">
                                No account yet. Set a password above (or leave blank for default) and click <b>Save</b> to create one.
                              </div>
                            )}
                          </div>
                          <div className="d-flex gap-2">
                            {(initial.auth?.email || initial.email || form.email) && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  title="Copy login email"
                                  onClick={() => {
                                    const em = initial.auth?.email || initial.email || form.email;
                                    navigator.clipboard.writeText(em).catch(() => {});
                                  }}
                                >
                                  <i className="bi bi-clipboard" /> Copy
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-success"
                                  title="Send password reset email"
                                  onClick={async () => {
                                    const em = initial.auth?.email || initial.email || form.email;
                                    if (!em) return Swal.fire({ icon: "info", title: "No email on file" });
                                    try {
                                      await sendPasswordResetEmail(getAuth(), em);
                                      Swal.fire({ icon: "success", title: "Reset email sent", text: `Sent to ${em}`, timer: 1600, showConfirmButton: false });
                                    } catch (e) {
                                      Swal.fire({ icon: "error", title: "Could not send reset email", text: e?.message || "Try again" });
                                    }
                                  }}
                                >
                                  <i className="bi bi-envelope-paper" /> Reset
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                ) : null}

                <Col md={12}>
                  <FloatingLabel label="Address">
                    <Form.Control as="textarea" style={{ height: 84 }} value={form.address} onChange={(e)=>change("address", e.target.value)} placeholder="Street, City" />
                  </FloatingLabel>
                </Col>
              </Row>
            </Col>

            {/* Avatar */}
            <Col lg={4}>
              <div
                className={`border rounded-3 p-3 text-center avatar-drop ${dragOver ? "bg-light" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); acceptFile(e.dataTransfer.files?.[0]); }}
              >
                <div className="mb-2 fw-semibold">Profile Photo</div>
                <div className="d-flex justify-content-center mb-2">
                  {previewUrl ? (
                    <img src={previewUrl} alt="Preview" className="avatar-hero" />
                  ) : (
                    <div className="d-inline-flex align-items-center justify-content-center bg-light border avatar-hero" style={{ color: "#6c757d" }}>
                      <i className="bi bi-person fs-1" />
                    </div>
                  )}
                </div>
                <div className="d-grid gap-2">
                  <Button variant="outline-primary" type="button" onClick={() => fileRef.current?.click()}>
                    Choose Image
                  </Button>
                  {previewUrl && (
                    <Button variant="outline-secondary" type="button" onClick={() => { setForm((f) => ({ ...f, photoFile: null })); }}>
                      Clear Selection
                    </Button>
                  )}
                  {initial?.photoURL && !form.photoFile && (
                    <Form.Check className="mt-1" type="switch" id="remove-photo" label="Remove existing photo"
                      checked={!!form.removePhoto} onChange={(e) => change("removePhoto", e.target.checked)} />
                  )}
                </div>
                <input ref={fileRef} type="file" hidden accept="image/png,image/jpeg,image/webp" onChange={(e) => acceptFile(e.target.files?.[0] || null)} />
                <div className="form-text mt-2">PNG/JPG/WEBP • ~2MB</div>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

/* ---------------- Bulk upload modal ---------------- */
function BulkUploadModal({ show, onHide, onImport }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoCreateAccounts, setAutoCreateAccounts] = useState(true);

  useEffect(() => { if (!show) setRows([]); }, [show]);

  const downloadTemplate = (fmt = "xlsx") => {
    const template = [{
      name: "Jane Doe", rollNo: "12", className: "Class 12 PCM", section: "A", branch: "PCM", gender: "female",
      dob: "2009-06-14", phone: "+91 90000 00000", email: "jane.doe@example.com",
      address: "Street, City", admissionNo: "ADM123", status: "active", photoURL: ""
    }];
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "students_template");
    XLSX.writeFile(wb, `students_template.${fmt === "csv" ? "csv" : "xlsx"}`);
  };

  const parseFile = async (file) => {
    setLoading(true);
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws, { defval: "" });

      const norm = (k) => String(k || "").trim().toLowerCase();
      const parsed = json.map((r) => {
        const obj = {}; for (const [k, v] of Object.entries(r)) obj[norm(k)] = v;
        return {
          name: obj.name || "",
          rollNo: obj.rollno || obj["roll no"] || obj.roll || "",
          className: obj.classname || obj.class || "",
          section: obj.section || "",
          branch: obj.branch || obj.stream || "",
          gender: (obj.gender || "").toLowerCase(),
          dob: obj.dob || "",
          phone: obj.phone || obj.mobile || "",
          email: (obj.email || "").toLowerCase(),
          address: obj.address || "",
          admissionNo: obj.admissionno || obj["admission no"] || "",
          photoURL: obj.photourl || obj["photo url"] || "",
          status: obj.status || "active",
        };
      }).filter(r => r.name);

      setRows(parsed);
    } catch (e) {
      await Swal.fire({ icon: "error", title: "Could not parse file", text: e?.message || "Try another file." });
    } finally {
      setLoading(false);
    }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/\.(xlsx?|csv)$/i.test(f.name)) return Swal.fire({ icon: "info", title: "Use .xlsx or .csv file" });
    parseFile(f);
  };

  const startImport = async () => {
    if (!rows.length) return Swal.fire({ icon: "info", title: "No rows to import" });
    onImport({ rows, autoCreateAccounts });
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton><Modal.Title>Bulk Upload Students</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="mb-2 text-muted">
          Upload an <b>XLSX/CSV</b> with columns:&nbsp;
          <code>name*</code>, <code>rollNo</code>, <code>className</code>, <code>section</code>, <code>branch</code>/<code>stream</code>, <code>gender</code>, <code>dob</code>, <code>phone</code>, <code>email</code>, <code>address</code>, <code>admissionNo</code>, <code>photoURL</code>.
          <div className="mt-1">Accounts will be created <b>only</b> for rows that include a valid <code>email</code>.</div>
        </div>

        <div className="d-flex gap-2 mb-3">
          <Button variant="outline-secondary" onClick={() => downloadTemplate("xlsx")}>
            <i className="bi bi-file-earmark-arrow-down me-1" /> XLSX template
          </Button>
          <Button variant="outline-secondary" onClick={() => downloadTemplate("csv")}>
            <i className="bi bi-file-earmark-arrow-down me-1" /> CSV template
          </Button>
        </div>

        <Form.Group className="mb-3">
          <Form.Control type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} disabled={loading} />
        </Form.Group>

        <Form.Check
          type="switch" id="auto-create"
          checked={autoCreateAccounts}
          onChange={(e) => setAutoCreateAccounts(e.target.checked)}
          label="Create login accounts for rows that have an email"
        />

        {loading ? (
          <div className="text-center py-4"><Spinner animation="border" size="sm" /> Parsing…</div>
        ) : rows.length ? (
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white small">Preview ({rows.length} rows) – first 10</Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0 align-middle">
                  <thead><tr><th>Name</th><th>Class</th><th>Section</th><th>Branch</th><th>Roll</th><th>Email</th><th>DOB</th></tr></thead>
                  <tbody>
                    {rows.slice(0, 10).map((r, i) => (
                      <tr key={i}>
                        <td>{r.name}</td><td>{r.className}</td><td>{r.section}</td><td>{r.branch}</td><td>{r.rollNo}</td><td>{r.email}</td><td>{r.dob}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
          </Card>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>Close</Button>
        <Button onClick={startImport} disabled={!rows.length}>Import</Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ---------------- main page ---------------- */
export default function Students() {
  const { school, loading: loadingSchool } = useSchool();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters
  const [qText, setQText] = useState("");
  const [cls, setCls] = useState("all");
  const [sec, setSec] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // modal
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);

  // bulk
  const [showBulk, setShowBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });

  const searchRef = useRef(null);

  // live students
  useEffect(() => {
    if (!school?.id) return;
    setLoading(true);
    const col = collection(db, "schools", school.id, "students");
    const unsub = onSnapshot(
      query(col, orderBy("createdAt", "desc")),
      (snap) => {
        const list = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            name: data.name || "—",
            rollNo: data.rollNo || "",
            className: data.className || "",
            section: data.section || "",
            branch: data.branch || "",
            gender: data.gender || "",
            dob: toDate(data.dob),
            phone: data.phone || "",
            email: data.email || "",
            address: data.address || "",
            admissionNo: data.admissionNo || "",
            status: data.status || "active",
            photoURL: data.photoURL || "",
            auth: data.auth || null, // { uid, email }
            createdAt: toDate(data.createdAt),
          };
        });
        setRows(list);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [school?.id]);

  const classesList = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.className && s.add(r.className));
    return ["all", ...Array.from(s).sort()];
  }, [rows]);

  const sectionsList = useMemo(() => {
    const s = new Set();
    rows.forEach((r) => r.section && s.add(r.section));
    return ["all", ...Array.from(s).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = qText.trim().toLowerCase();
    return rows.filter((r) => {
      if (cls !== "all" && r.className !== cls) return false;
      if (sec !== "all" && r.section !== sec) return false;
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (q) {
        const hay = [r.name, r.rollNo, r.className, r.section, r.branch, r.email, r.phone, r.admissionNo, r.address]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, qText, cls, sec, statusFilter]);

  const openCreate = () => { setEditing(null); setShowModal(true); };
  const openEdit = (row) => { setEditing(row); setShowModal(true); };

  // Upload helper
  const uploadAvatar = async (studentId, file) => {
    if (!file) return null;
    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `schools/${school.id}/students/${studentId}/avatar_${Date.now()}.${ext}`;
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file, { contentType: file.type });
    return getDownloadURL(storageRef);
  };

  // Create or update Firestore + Auth (single)
  const save = async (payload) => {
    const base = {
      name: payload.name,
      rollNo: payload.rollNo,
      className: payload.className,
      section: payload.section,
      branch: payload.branch,
      gender: payload.gender,
      dob: payload.dob ? new Date(payload.dob) : null,
      phone: payload.phone,
      email: payload.email || null,
      address: payload.address,
      admissionNo: payload.admissionNo,
      status: payload.status || "active",
      updatedAt: serverTimestamp(),
    };

    if (editing) {
      await updateDoc(doc(db, "schools", school.id, "students", editing.id), {
        ...base,
        ...(payload.removePhoto && !payload.photoFile ? { photoURL: "" } : {}),
      });

      if (payload.photoFile) {
        const url = await uploadAvatar(editing.id, payload.photoFile);
        await updateDoc(doc(db, "schools", school.id, "students", editing.id), {
          photoURL: url || "",
          updatedAt: serverTimestamp(),
        });
      }

      // Create Auth user if not already present
      if (payload.email && !editing.auth?.uid) {
        const tempPassword =
          (payload.password || "").trim() ||
          defaultPassFrom(payload.rollNo, payload.admissionNo);

        try {
          const user = await createStudentAuthUser({
            email: payload.email,
            password: tempPassword,
            displayName: payload.name,
          });

          await Promise.all([
            updateDoc(doc(db, "schools", school.id, "students", editing.id), {
              auth: { uid: user.uid, email: user.email },
              updatedAt: serverTimestamp(),
            }),
            setDoc(doc(db, "users", user.uid), {
              role: "student",
              status: base.status,
              schoolId: school.id,
              studentId: editing.id,
              email: user.email,
              displayName: payload.name || "",
              className: payload.className || "",
              section: payload.section || "",
              branch: payload.branch || "",
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            }, { merge: true }),
          ]);

          await Swal.fire({
            icon: "success",
            title: "Student updated & account created",
            html: `
              <div style="text-align:left;line-height:1.55">
                <p><strong>Login ID (email):</strong> ${user.email}</p>
                <p><strong>Password:</strong> <code>${tempPassword}</code></p>
                <p class="text-muted">Share credentials with the student; they should change the password after first login.</p>
              </div>
            `,
            showCancelButton: true,
            confirmButtonText: "Copy",
            cancelButtonText: "Close",
            preConfirm: () => {
              const t = `Email: ${user.email}\nPassword: ${tempPassword}`;
              return navigator.clipboard.writeText(t).catch(() => {});
            },
          });
        } catch (e) {
          Swal.fire({ icon: "error", title: "Auth creation failed", text: e?.message || "Try again" });
        }
      } else {
        Swal.fire({ icon: "success", title: "Updated", timer: 900, showConfirmButton: false });
      }
    } else {
      // Create Firestore doc first
      const refDoc = await addDoc(collection(db, "schools", school.id, "students"), {
        ...base,
        photoURL: "",
        createdAt: serverTimestamp(),
      });

      if (payload.photoFile) {
        const url = await uploadAvatar(refDoc.id, payload.photoFile);
        await updateDoc(refDoc, { photoURL: url || "", updatedAt: serverTimestamp() });
      }

      // Create Auth user if email provided
      if (payload.email) {
        const tempPassword =
          (payload.password || "").trim() ||
          defaultPassFrom(payload.rollNo, payload.admissionNo);

        try {
          const user = await createStudentAuthUser({
            email: payload.email,
            password: tempPassword,
            displayName: payload.name,
          });

          await Promise.all([
            updateDoc(refDoc, {
              auth: { uid: user.uid, email: user.email },
              updatedAt: serverTimestamp(),
            }),
            setDoc(doc(db, "users", user.uid), {
              role: "student",
              status: payload.status || "active",
              schoolId: school.id,
              studentId: refDoc.id,
              email: user.email,
              displayName: payload.name || "",
              className: payload.className || "",
              section: payload.section || "",
              branch: payload.branch || "",
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            }, { merge: true }),
          ]);

          await Swal.fire({
            icon: "success",
            title: "Student added & account created",
            html: `
              <div style="text-align:left;line-height:1.55">
                <p><strong>Login ID (email):</strong> ${user.email}</p>
                <p><strong>Password:</strong> <code>${tempPassword}</code></p>
                <p class="text-muted">Share credentials with the student; they should change the password after first login.</p>
              </div>
            `,
            showCancelButton: true,
            confirmButtonText: "Copy",
            cancelButtonText: "Close",
            preConfirm: () => {
              const t = `Email: ${user.email}\nPassword: ${tempPassword}`;
              return navigator.clipboard.writeText(t).catch(() => {});
            },
          });
        } catch (e) {
          Swal.fire({ icon: "error", title: "Auth creation failed", text: e?.message || "Try again" });
        }
      } else {
        Swal.fire({ icon: "success", title: "Added", timer: 900, showConfirmButton: false });
      }
    }
  };

  const removeRow = async (row) => {
    const res = await Swal.fire({
      icon: "warning",
      title: "Delete student?",
      text: `Remove "${row.name}" (${row.className}${row.section ? "-" + row.section : ""}).`,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Delete",
    });
    if (!res.isConfirmed) return;
    await deleteDoc(doc(db, "schools", school.id, "students", row.id));
    Swal.fire({ icon: "success", title: "Deleted", timer: 900, showConfirmButton: false });
  };

  const exportCSV = () => {
    const data = filtered.map((r) => ({
      id: r.id,
      name: r.name,
      rollNo: r.rollNo,
      class: r.className,
      section: r.section,
      branch: r.branch,
      gender: r.gender,
      dob: fmt(r.dob, "YYYY-MM-DD"),
      phone: r.phone,
      email: r.email,
      admissionNo: r.admissionNo,
      status: r.status,
      photoURL: r.photoURL || "",
      createdAt: fmt(r.createdAt, "YYYY-MM-DD"),
      uid: r.auth?.uid || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "students");
    XLSX.writeFile(wb, "students_export.csv");
  };

  const handleSendReset = async (row) => {
    if (!row?.email) return Swal.fire({ icon: "info", title: "No email on file" });
    try {
      await sendPasswordResetEmail(getAuth(), row.email);
      Swal.fire({ icon: "success", title: "Reset email sent", text: `A password reset link was emailed to ${row.email}`, timer: 1600, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: "error", title: "Could not send reset email", text: e?.message || "Try again" });
    }
  };

  // -------- Bulk import handler --------
  const handleBulkImport = async ({ rows: toImport, autoCreateAccounts }) => {
    if (!school?.id) return;
    setShowBulk(false);
    setBulkBusy(true);
    setBulkProgress({ done: 0, total: toImport.length });

    const creds = [];
    let processed = 0;

    const genPassFor = (r) => defaultPassFrom(r.rollNo, r.admissionNo);

    const coerceDate = (raw) => {
      if (!raw) return null;
      const s = String(raw).trim();
      let d = null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) d = new Date(s);
      else if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
        const [dd, mm, yyyy] = s.split("/");
        d = new Date(`${yyyy}-${mm}-${dd}`);
      } else if (!Number.isNaN(Date.parse(s))) {
        d = new Date(s);
      }
      return d && !isNaN(d.getTime()) ? d : null;
    };

    for (const r of toImport) {
      try {
        const emailFromFile = (r.email || "").trim().toLowerCase();

        const base = {
          name: r.name,
          rollNo: r.rollNo || "",
          className: r.className || "",
          section: r.section || "",
          branch: r.branch || "",
          gender: r.gender || "",
          dob: coerceDate(r.dob),
          phone: r.phone || "",
          email: emailFromFile || null,
          address: r.address || "",
          admissionNo: r.admissionNo || "",
          status: (r.status || "active").toString().toLowerCase() === "inactive" ? "inactive" : "active",
          photoURL: "",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        const refDoc = await addDoc(collection(db, "schools", school.id, "students"), base);

        if (autoCreateAccounts && emailFromFile) {
          const password = genPassFor(r);
          try {
            const user = await createStudentAuthUser({
              email: emailFromFile,
              password,
              displayName: r.name,
            });

            await Promise.all([
              updateDoc(refDoc, {
                email: user.email,
                auth: { uid: user.uid, email: user.email },
                updatedAt: serverTimestamp(),
              }),
              setDoc(
                doc(db, "users", user.uid),
                {
                  role: "student",
                  schoolId: school.id,
                  studentId: refDoc.id,
                  email: user.email,
                  displayName: r.name || "",
                  className: r.className || "",
                  section: r.section || "",
                  branch: r.branch || "",
                  createdAt: serverTimestamp(),
                  updatedAt: serverTimestamp(),
                },
                { merge: true }
              ),
            ]);

            creds.push({ name: r.name, email: user.email, password });
          } catch {
            // continue
          }
        }
      } catch {
        // continue
      } finally {
        processed += 1;
        setBulkProgress({ done: processed, total: toImport.length });
      }
    }

    setBulkBusy(false);

    if (creds.length) {
      const ws = XLSX.utils.json_to_sheet(creds);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "credentials");
      const blob = XLSX.write(wb, { type: "array", bookType: "xlsx" });
      const url = URL.createObjectURL(new Blob([blob], { type: "application/octet-stream" }));

      const html = `
        <div style="text-align:left;line-height:1.6">
          <p><strong>Created ${creds.length} student accounts.</strong></p>
          <div style="max-height:180px;overflow:auto;border:1px solid #eee;border-radius:8px;padding:8px">
            ${creds.slice(0, 20).map(c => `<div><code>${c.email}</code> — <b>${c.password}</b> <span style="color:#666">(${c.name})</span></div>`).join("")}
            ${creds.length > 20 ? `<div style="color:#666">…and ${creds.length - 20} more</div>` : ""}
          </div>
          <p class="text-muted" style="margin-top:8px">Download the full list as XLSX.</p>
        </div>
      `;

      const result = await Swal.fire({
        icon: "success",
        title: "Bulk import completed",
        html,
        showDenyButton: true,
        confirmButtonText: "Copy first 10",
        denyButtonText: "Download XLSX",
      });

      if (result.isDenied) {
        const a = document.createElement("a");
        a.href = url;
        a.download = "student_credentials.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } else {
        const first10 = creds.slice(0, 10).map(c => `${c.email} - ${c.password}`).join("\n");
        navigator.clipboard.writeText(first10).catch(() => {});
      }
    } else {
      Swal.fire({ icon: "success", title: "Bulk import finished" });
    }
  };

  if (loadingSchool) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> Loading…
        </Card.Body>
      </Card>
    );
  }

  if (!school?.id) return <div className="text-muted">No school selected.</div>;

  return (
    <Container fluid className="p-0">
      {/* Header */}
      <Row className="mb-3 g-2 align-items-center">
        <Col>
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-0">Students</h5>
          </div>
          <small className="text-muted">Manage student records for {school.name}</small>
        </Col>
        <Col className="text-end">
          <ButtonGroup className="action-toolbar gap-2">
            <Button
              variant="outline-secondary"
              className="btn-elev px-3 py-2"
              onClick={exportCSV}
              aria-label="Export CSV"
            >
              <i className="bi bi-download me-2" /> Export CSV
            </Button>

            <Button
              variant="outline-primary"
              className="btn-elev px-3 py-2"
              onClick={() => setShowBulk(true)}
              aria-label="Bulk Upload"
            >
              <i className="bi bi-upload me-2" /> Bulk Upload
            </Button>

            <Button
              className="btn-elev px-3 py-2"
              onClick={openCreate}
              aria-label="Add Student"
            >
              <i className="bi bi-plus-lg me-2" /> Add Student
            </Button>
          </ButtonGroup>
        </Col>
      </Row>

      {/* Bulk progress banner */}
      {bulkBusy && (
        <Card className="border-0 shadow-sm mb-3">
          <Card.Body className="py-3">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div className="fw-semibold">Importing students…</div>
                <div className="small text-muted">{bulkProgress.done} / {bulkProgress.total}</div>
              </div>
              <div style={{ minWidth: 240 }}>
                <ProgressBar now={bulkProgress.total ? (bulkProgress.done / bulkProgress.total) * 100 : 0} />
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      <Card className="p-3 shadow-sm glass-card">
        {/* Filters */}
        <div className="filter-bar sticky-top pb-2" style={{ top: 68 }}>
          <Row className="g-2 align-items-end">
            <Col lg={5}>
              <Form.Label className="mb-1">Search</Form.Label>
              <InputGroup className="elevated-input">
                <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
                <Form.Control
                  ref={searchRef}
                  placeholder="Name, roll, class, branch, email, phone…"
                  value={qText}
                  onChange={(e) => setQText(e.target.value)}
                />
                {qText && (
                  <Button variant="outline-secondary" onClick={() => setQText("")} title="Clear">
                    <i className="bi bi-x" />
                  </Button>
                )}
              </InputGroup>
            </Col>
            <Col md={3} lg={2}>
              <Form.Label className="mb-1">Class</Form.Label>
              <Form.Select value={cls} onChange={(e) => setCls(e.target.value)}>
                {classesList.map((c) => <option key={c} value={c}>{c === "all" ? "All" : c}</option>)}
              </Form.Select>
            </Col>
            <Col md={3} lg={2}>
              <Form.Label className="mb-1">Section</Form.Label>
              <Form.Select value={sec} onChange={(e) => setSec(e.target.value)}>
                {sectionsList.map((s) => <option key={s} value={s}>{s === "all" ? "All" : s}</option>)}
              </Form.Select>
            </Col>
            <Col md={6} lg={3}>
              <Form.Label className="mb-1">Status</Form.Label>
              <div className="btn-chip-group">
                {["all", "active", "inactive"].map((key) => (
                  <Button
                    key={key}
                    size="sm"
                    variant={statusFilter === key ? "primary" : "outline-secondary"}
                    className="chip"
                    onClick={() => setStatusFilter(key)}
                  >
                    {key[0].toUpperCase() + key.slice(1)}
                  </Button>
                ))}
              </div>
            </Col>
            <Col md={12} className="text-end d-none d-lg-block">
              <Badge bg="light" text="dark" className="border">
                {filtered.length} of {rows.length} shown
              </Badge>
            </Col>
          </Row>
        </div>

        {/* Table */}
        <div className="table-responsive mt-2">
          <Table hover className="align-middle modern-table">
            <thead className="position-sticky top-0">
              <tr>
                <th className="d-none d-sm-table-cell">Avatar</th>
                <th>Name</th>
                <th className="d-none d-md-table-cell">Roll</th>
                <th>Class</th>
                <th className="d-none d-md-table-cell">Branch</th>
                <th className="d-none d-md-table-cell">Gender</th>
                <th className="d-none d-lg-table-cell">DOB</th>
                <th className="d-none d-lg-table-cell">Phone</th>
                <th className="d-none d-xl-table-cell">Email</th>
                <th className="d-none d-xl-table-cell">Account</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} className="text-center py-5">
                    <Spinner animation="border" size="sm" /> Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center text-muted py-4">No students found</td>
                </tr>
              ) : (
                filtered.map((s) => (
                  <tr key={s.id} className="row-hover">
                    <td className="d-none d-sm-table-cell" style={{ width: 56 }}>
                      {s.photoURL ? (
                        <img src={s.photoURL} alt={s.name} className="avatar-sm" />
                      ) : (
                        <div className="avatar-sm placeholder" title={s.name}>
                          {initials(s.name)}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="fw-semibold text-truncate" style={{ maxWidth: 220 }}>{s.name}</div>
                      <div className="text-muted small">{s.admissionNo || s.id}</div>
                    </td>
                    <td className="d-none d-md-table-cell">{s.rollNo || "—"}</td>
                    <td>{[s.className, s.section].filter(Boolean).join("-") || "—"}</td>
                    <td className="d-none d-md-table-cell">{s.branch || "—"}</td>
                    <td className="d-none d-md-table-cell text-capitalize">{s.gender || "—"}</td>
                    <td className="d-none d-lg-table-cell">{fmt(s.dob)}</td>
                    <td className="d-none d-lg-table-cell">{s.phone || "—"}</td>
                    <td className="d-none d-xl-table-cell">{s.email || "—"}</td>
                    <td className="d-none d-xl-table-cell">
                      {s.auth?.uid ? (
                        <>
                          {s.status === "active"
                            ? <Badge bg="success" className="me-2">Active</Badge>
                            : <Badge bg="secondary" className="me-2">Inactive</Badge>}
                          <span className="text-muted small">{s.auth.email || s.email}</span>
                        </>
                      ) : (
                        <Badge bg="secondary">No account</Badge>
                      )}
                    </td>
                    <td className="text-end">
                      <Dropdown align="end">
                        <Dropdown.Toggle size="sm" variant="outline-secondary" className="rounded-3">
                          <i className="bi bi-three-dots-vertical" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          {s.email && (
                            <>
                              <Dropdown.Item onClick={() => navigator.clipboard.writeText(s.email).catch(() => {})}>
                                <i className="bi bi-clipboard me-2" /> Copy email
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => handleSendReset(s)}>
                                <i className="bi bi-envelope-paper me-2" /> Send reset email
                              </Dropdown.Item>
                              <Dropdown.Divider />
                            </>
                          )}
                          <Dropdown.Item onClick={() => openEdit(s)}>
                            <i className="bi bi-pencil me-2" /> Edit
                          </Dropdown.Item>
                          <Dropdown.Item className="text-danger" onClick={() => removeRow(s)}>
                            <i className="bi bi-trash me-2" /> Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      {/* Single Add/Edit modal */}
      <StudentFormModal
        show={showModal}
        onHide={() => setShowModal(false)}
        initial={editing}
        onSave={save}
      />

      {/* Bulk Upload modal */}
      <BulkUploadModal show={showBulk} onHide={() => setShowBulk(false)} onImport={handleBulkImport} />

      

      <style>{`
        :root{ --ring: rgba(13,110,253,.45); --accent-12: rgba(13,110,253,.10); }
        [data-bs-theme="dark"]{ --ring: rgba(13,110,253,.6); --accent-12: rgba(13,110,253,.18); }

        .card { border-radius: 14px; }
        .form-control, .form-select { border-radius: 10px; }

        .glass-card{ background: color-mix(in oklab, var(--bs-body-bg) 92%, transparent); backdrop-filter: blur(8px); }
        .elevated-input .form-control:focus{ box-shadow: 0 0 0 .2rem var(--ring); }
        .btn-chip-group .chip{ border-radius: 999px; padding-inline: .85rem; }

        .modern-table thead th{ background: var(--bs-body-bg); z-index: 1; }
        .row-hover:hover{ background: color-mix(in oklab, var(--bs-primary) 5%, transparent); transition: background .18s ease, transform .18s ease; }

        .avatar-sm{ height: 36px; width: 36px; border-radius: 10px; object-fit: cover; border: 1px solid var(--bs-border-color); }
        .avatar-sm.placeholder{ display:flex; align-items:center; justify-content:center; height:36px; width:36px; border-radius:10px; background: var(--bs-tertiary-bg); border: 1px dashed var(--bs-border-color); font-size:12px; font-weight:700; color:#6c757d; }
        .avatar-hero{ width: 96px; height: 96px; object-fit: cover; border-radius: 50%; border: 2px solid var(--bs-border-color); }
        .avatar-drop{ transition: border-color .18s ease, background .18s ease; }
        .avatar-drop:hover{ border-color: var(--bs-primary); background: var(--accent-12); }

        .filter-bar{
          background: color-mix(in oklab, var(--bs-body-bg) 96%, transparent);
          backdrop-filter: blur(6px);
          border-bottom: 1px solid var(--bs-border-color);
          margin: -16px -16px 12px -16px; padding: 12px 16px 8px 16px;
          border-top-left-radius: 12px; border-top-right-radius: 12px;
        }
      `}</style>
    </Container>
  );
}
